/**
 * What this browser can actually do, probed once at start-up.
 *
 * The product's core loop needs four things and degrades differently without each of
 * them, so the probe reports them individually rather than returning a single boolean.
 * A screen that says "this browser cannot do X" is a screen someone can act on; "not
 * supported" is not.
 *
 * Everything is read through an injected environment object so this is testable under
 * Node without a DOM — which is the point of it being a `.ts` file rather than living
 * inside a component.
 */

export type CapabilityId = 'secureContext' | 'webAudio' | 'audioWorklet' | 'microphone' | 'storage';

export interface CapabilityInfo {
  /** What is missing, in the user's words, not the spec's. */
  label: string;
  /** What stops working without it. */
  consequence: string;
}

export const CAPABILITY_INFO: Readonly<Record<CapabilityId, CapabilityInfo>> = Object.freeze({
  secureContext: {
    label: 'A secure connection',
    consequence: 'Browsers only allow microphone access over HTTPS. Open this page over https://.',
  },
  webAudio: {
    label: 'Web Audio',
    consequence: 'Without it there is no way to analyse what the piano is playing.',
  },
  audioWorklet: {
    label: 'AudioWorklet',
    consequence: 'The chord recogniser runs on the audio thread and needs this to keep up.',
  },
  microphone: {
    label: 'Microphone access',
    consequence: 'This browser exposes no way to ask for a microphone.',
  },
  storage: {
    label: 'On-device storage',
    consequence: 'Sketches cannot be saved, and a refresh will lose the session.',
  },
});

/**
 * The structural subset of `window` the probe reads. A real `window` satisfies it, so
 * production passes `globalThis` straight in.
 */
export interface CapabilityProbeEnv {
  isSecureContext?: boolean;
  navigator?: { mediaDevices?: { getUserMedia?: unknown } | undefined } | undefined;
  AudioContext?: unknown;
  webkitAudioContext?: unknown;
  AudioWorkletNode?: unknown;
  indexedDB?: unknown;
}

export interface CapabilityReport {
  /** True when the listening loop can run at all. */
  supported: boolean;
  /**
   * True when the player can still use the app by typing chords in. This is almost
   * always true, and it is why an unsupported browser gets an offer rather than a wall.
   */
  manualEntryPossible: boolean;
  /** True when sketches can be persisted. Independent of `supported`. */
  canSave: boolean;
  present: CapabilityId[];
  missing: CapabilityId[];
  detail: Readonly<Record<CapabilityId, boolean>>;
}

/** Capabilities without which there is no listening loop. */
const REQUIRED_FOR_LISTENING: readonly CapabilityId[] = Object.freeze([
  'secureContext',
  'webAudio',
  'audioWorklet',
  'microphone',
]);

const ALL_CAPABILITIES: readonly CapabilityId[] = Object.freeze([
  'secureContext',
  'webAudio',
  'audioWorklet',
  'microphone',
  'storage',
]);

/**
 * Probe the environment.
 *
 * Note the `storage` probe only checks that `indexedDB` exists. Safari in Lockdown Mode
 * and some private-browsing configurations expose the object and then throw on `open()`,
 * which cannot be detected synchronously — the persistence layer has to handle that
 * failure anyway, so probing harder here would buy nothing.
 */
export function probeCapabilities(env: CapabilityProbeEnv = globalThis): CapabilityReport {
  const detail: Record<CapabilityId, boolean> = {
    secureContext: env.isSecureContext === true,
    webAudio:
      typeof env.AudioContext === 'function' || typeof env.webkitAudioContext === 'function',
    audioWorklet: typeof env.AudioWorkletNode === 'function',
    microphone: typeof env.navigator?.mediaDevices?.getUserMedia === 'function',
    storage: env.indexedDB !== undefined && env.indexedDB !== null,
  };

  const present = ALL_CAPABILITIES.filter((id) => detail[id]);
  const missing = ALL_CAPABILITIES.filter((id) => !detail[id]);

  return {
    supported: REQUIRED_FOR_LISTENING.every((id) => detail[id]),
    // Typing a chord symbol needs nothing but a keyboard, so this is unconditional.
    // It exists as a field so the screens can read it rather than assume it.
    manualEntryPossible: true,
    canSave: detail.storage,
    present,
    missing,
    detail: Object.freeze(detail),
  };
}

/** A report in which everything works. Fixtures and tests start from this. */
export function fullCapabilityReport(): CapabilityReport {
  return {
    supported: true,
    manualEntryPossible: true,
    canSave: true,
    present: [...ALL_CAPABILITIES],
    missing: [],
    detail: Object.freeze({
      secureContext: true,
      webAudio: true,
      audioWorklet: true,
      microphone: true,
      storage: true,
    }),
  };
}
